import { Flex, VStack } from "@chakra-ui/react";
import { TableCell, TableRow } from "toolkit/chakra/table";

import AddressFromTo from "ui/shared/address/AddressFromTo";
import { Badge } from "toolkit/chakra/badge";
import BlockEntity from "ui/shared/entities/block/BlockEntity";
import BlockPendingUpdateHint from "ui/shared/block/BlockPendingUpdateHint";
import type { ChainConfig } from "types/multichain";
import ChainIcon from "ui/optimismSuperchain/components/ChainIcon";
import CurrencyValue from "ui/shared/CurrencyValue";
import DotoneCellLogo from "ui/shared/DotoneCellLogo";
import { Image } from "toolkit/chakra/image";
import React from "react";
import TimeWithTooltip from "ui/shared/time/TimeWithTooltip";
import type { Transaction } from "types/api/transaction";
import TxAdditionalInfo from "ui/txs/TxAdditionalInfo";
import TxEntity from "ui/shared/entities/tx/TxEntity";
import TxFee from "ui/shared/tx/TxFee";
import TxStatus from "ui/shared/statusTag/TxStatus";
import TxTranslationType from "./TxTranslationType";
import TxType from "./TxType";
import TxWatchListTags from "ui/shared/tx/TxWatchListTags";
import config from "configs/app";
import { isDotoneCellTransaction } from "tools/utils/checkDotoneCellAddress";
import { isMyDotAddress } from "tools/utils/checkMyDotContracts";

type Props = {
  tx: Transaction;
  showBlockInfo: boolean;
  currentAddress?: string;
  enableTimeIncrement?: boolean;
  isLoading?: boolean;
  animation?: string;
  chainData?: ChainConfig;
  timeTick?: number;
};

const TxsTableItem = ({
  tx,
  showBlockInfo,
  currentAddress,
  enableTimeIncrement,
  isLoading,
  animation,
  chainData,
  timeTick,
}: Props) => {
  const dataTo = tx.to ? tx.to : tx.created_contract;
  const isMyDotTx = isMyDotAddress(tx?.from?.hash);

  return (
    <TableRow key={tx.hash} animation={animation} paddingBottom={0}>
      <TableCell textAlign="center">
        <div className="flex flex-col h-full justify-between">
          <TxAdditionalInfo tx={tx} isLoading={isLoading} />
          {!isLoading && isDotoneCellTransaction(tx) && (
            <span className="relative top-2">
              <DotoneCellLogo />
            </span>
          )}
          {isMyDotTx && (
            <Image
              src="/assets/logo.svg"
              width={24}
              height={24}
              onClick={() =>
                window.open(
                  "https://mydot.one",
                  "_blank",
                  "noopener,noreferrer",
                )
              }
              alt="doto logo"
              className="mydot-transaction-logo w-5.5! h-5.5! cursor-pointer relative top-2 light-shadow-gold rounded-full"
            />
          )}
        </div>
      </TableCell>
      {chainData && (
        <TableCell>
          <ChainIcon data={chainData} isLoading={isLoading} my="2px" />
        </TableCell>
      )}
      <TableCell pr={4}>
        <VStack alignItems="start" lineHeight="24px">
          <TxEntity
            hash={tx.hash}
            isLoading={isLoading}
            fontWeight="bold"
            noIcon
            maxW="100%"
            truncation="constant"
          />
          <TimeWithTooltip
            timestamp={tx.timestamp}
            enableIncrement={enableTimeIncrement}
            isLoading={isLoading}
            color="text.secondary"
            refreshTick={timeTick}
          />
        </VStack>
      </TableCell>
      <TableCell>
        <VStack alignItems="start">
          {tx.translation ? (
            <TxTranslationType
              types={tx.transaction_types}
              isLoading={isLoading || tx.translation.isLoading}
              translatationType={tx.translation.data?.type}
            />
          ) : (
            <TxType types={tx.transaction_types} isLoading={isLoading} />
          )}
          <TxStatus
            status={tx.status}
            errorText={tx.status === "error" ? tx.result : undefined}
            isLoading={isLoading}
          />
          <TxWatchListTags tx={tx} isLoading={isLoading} />
        </VStack>
      </TableCell>
      <TableCell whiteSpace="nowrap">
        {tx.method && (
          <Badge
            colorPalette={tx.method === "Multicall" ? "teal" : "gray"}
            loading={isLoading}
            truncated
          >
            <span>{tx.method}</span>
          </Badge>
        )}
      </TableCell>
      {showBlockInfo && (
        <TableCell>
          <Flex alignItems="center" gap={2}>
            {tx.block_number && (
              <BlockEntity
                isLoading={isLoading}
                number={tx.block_number}
                noIcon
                textStyle="sm"
                fontWeight={500}
              />
            )}
            {tx.is_pending_update && <BlockPendingUpdateHint view="tx" />}
          </Flex>
        </TableCell>
      )}
      <TableCell>
        <AddressFromTo
          from={tx.from}
          to={dataTo}
          current={currentAddress}
          isLoading={isLoading}
          mt="2px"
          mode="compact"
        />
      </TableCell>
      {!config.UI.views.tx.hiddenFields?.value && (
        <TableCell isNumeric>
          <CurrencyValue
            value={tx.value}
            accuracy={8}
            isLoading={isLoading}
            wordBreak="break-word"
          />
        </TableCell>
      )}
      {!config.UI.views.tx.hiddenFields?.tx_fee && (
        <TableCell isNumeric maxW="220px">
          <TxFee
            tx={tx}
            accuracy={8}
            isLoading={isLoading}
            withCurrency={Boolean(tx.celo || tx.stability_fee)}
            justifyContent="end"
            wordBreak="break-word"
          />
        </TableCell>
      )}
    </TableRow>
  );
};

export default React.memo(TxsTableItem);
